import { createFileRoute, useParams } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  CopyButton,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

function PageEditor() {
  const { uid } = useParams({ from: '/central/cms-pages/$uid' });
  const page = trpc.admin.pages.getByUid.useQuery({ uid });
  const utils = trpc.useUtils();
  const update = trpc.admin.pages.update.useMutation({
    onSuccess: () => {
      void utils.admin.pages.getByUid.invalidate({ uid });
      void utils.admin.pages.list.invalidate();
      notifications.show({ message: 'Saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showInMenu, setShowInMenu] = useState(false);

  useEffect(() => {
    if (page.data) {
      setTitle(page.data.title);
      setContent(page.data.content ?? '');
      setShowInMenu(page.data.showInMenu === 1);
    }
  }, [page.data]);

  if (page.isPending) return <Skeleton height={400} />;
  if (page.isError || !page.data) return <Text c="red">{page.error?.message ?? 'Not found'}</Text>;

  const publicUrl = `/page/${page.data.slug}`;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={2}>{page.data.title}</Title>
            <Group gap="xs">
              <Anchor href={publicUrl} target="_blank" size="sm" ff="monospace">
                {publicUrl} <IconExternalLink size={12} style={{ display: 'inline' }} />
              </Anchor>
              <CopyButton value={publicUrl}>
                {({ copy, copied }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy URL'}>
                    <ActionIcon variant="subtle" size="sm" onClick={copy}>
                      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Stack>
          <Button
            loading={update.isPending}
            leftSection={<IconCheck size={14} />}
            onClick={() =>
              update.mutate({
                pageId: page.data!.id,
                title,
                content,
                showInMenu: showInMenu ? 1 : 0,
              })
            }
          >
            Save
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Card>
            <Stack gap="sm">
              <Title order={5}>Edit</Title>
              <TextInput
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
              />
              <Textarea
                label="HTML content"
                description="Plain HTML is rendered as-is to public visitors."
                minRows={18}
                autosize
                value={content}
                onChange={(e) => setContent(e.currentTarget.value)}
              />
              <Switch
                label="Show in menu"
                checked={showInMenu}
                onChange={(e) => setShowInMenu(e.currentTarget.checked)}
              />
            </Stack>
          </Card>

          <Card>
            <Stack gap="sm">
              <Group justify="space-between">
                <Title order={5}>Preview</Title>
                <Text c="dimmed" size="xs">
                  Live preview — re-renders on every keystroke.
                </Text>
              </Group>
              <div
                style={{
                  padding: 16,
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 8,
                  minHeight: 360,
                  background: 'var(--mantine-color-body)',
                }}
              >
                <Title order={2} mb="md">
                  {title || (
                    <Text component="span" c="dimmed">
                      (untitled)
                    </Text>
                  )}
                </Title>
                {content ? (
                  // CA-024: sanitize CMS HTML (admin preview) — defense-in-depth.
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
                ) : (
                  <Text c="dimmed">(empty content)</Text>
                )}
              </div>
            </Stack>
          </Card>
        </SimpleGrid>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/cms-pages/$uid')({ component: PageEditor });
